import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import cn from 'classnames';

import FormSelectStatic from './FormSelectStatic';

const MAP_TYPE = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

const generateArrOfMonths = () =>
  Array.from({ length: 12 }, (item, i) => ({
    label: new Date(0, i).toLocaleString('en-US', { month: 'long' }),
    value: i,
  }));

const generateArrOfYears = (prevYears) => {
  const currentYear = dayjs();
  const x = prevYears.map((item) => {
    const y = dayjs(currentYear.subtract(item, 'year').toDate()).format('YYYY');
    return {
      label: y,
      value: y,
    };
  });

  const formatCurrYear = currentYear.format('YYYY');

  return [...x, { label: formatCurrYear, value: formatCurrYear }] || [];
};

const FormSelectDate = ({
  onChange,
  prevYears,
  label,
  yearOnly,
  defaultMode,
  containerClassName,
  innerContainerClassName,
  yearContainerClassName,
  monthContainerClassName,
}) => {
  const monthsOptions = generateArrOfMonths();
  const yearsOptions = generateArrOfYears(prevYears);
  const [dates, setDates] = useState({
    months: {
      label: '',
      value: '',
    },
    years: { label: '', value: '' },
  });

  const getYear = useMemo(
    () => dates.years.value || new Date().getFullYear(),
    [dates],
  );

  const getMonth = useMemo(
    () => dates.months.value || new Date().getMonth(),
    [dates],
  );

  useEffect(() => {
    const modifiedDate = dayjs(new Date(getYear, getMonth));

    const newState = {
      type: MAP_TYPE[defaultMode],
      date_from: dayjs(modifiedDate)
        .startOf(defaultMode)
        .format('YYYY-MM-DD 00:00:00'),
      date_to: dayjs(modifiedDate)
        .endOf(defaultMode)
        .format('YYYY-MM-DD 23:59:59'),
    };

    onChange((prev) => ({
      ...prev,
      ...newState,
    }));
  }, [onChange, dates, defaultMode, getMonth, getYear]);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-x-2',
        containerClassName,
      )}
    >
      {label && (
        <label htmlFor="date" className="text-sm font-bold cursor-pointer">
          {label}
        </label>
      )}

      {yearOnly ? (
        <FormSelectStatic
          id="dates"
          name="years"
          placeholder="Year"
          onChange={setDates}
          value={dates.years}
          data={yearsOptions}
          inputClassName="h-[35px] !bg-white "
          containerClassName={cn(
            '!w-auto sm:!w-[120px] shadow-md',
            yearContainerClassName,
          )}
        />
      ) : (
        <div className={cn('flex', innerContainerClassName)}>
          <FormSelectStatic
            id="dates"
            name="months"
            placeholder="Month"
            onChange={setDates}
            value={dates.months}
            data={monthsOptions}
            inputClassName="h-[35px] !bg-white"
            containerClassName={cn(
              '!w-auto sm:!w-[120px] shadow-md',
              monthContainerClassName,
            )}
          />
          <FormSelectStatic
            id="dates"
            name="years"
            placeholder="Year"
            onChange={setDates}
            value={dates.years}
            data={yearsOptions}
            inputClassName="h-[35px] !bg-white "
            containerClassName={cn(
              '!w-auto sm:!w-[120px] shadow-md',
              yearContainerClassName,
            )}
          />
        </div>
      )}
    </div>
  );
};

FormSelectDate.defaultProps = {
  label: '',
  defaultMode: 'year',
  yearOnly: false,
  prevYears: [5, 4, 3, 2, 1],
  monthContainerClassName: '',
  yearContainerClassName: '',
  containerClassName: '',
  innerContainerClassName: '',
};

FormSelectDate.propTypes = {
  label: PropTypes.string,
  yearOnly: PropTypes.bool,
  innerContainerClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  monthContainerClassName: PropTypes.string,
  yearContainerClassName: PropTypes.string,
  defaultMode: PropTypes.string,
  prevYears: PropTypes.instanceOf(Array),
  onChange: PropTypes.func.isRequired,
};

export default FormSelectDate;
